import { isNil, omitBy, assign } from 'lodash-es';
import { DEALS_FILTER_VALUES, FILTER_QUERY_PARAMS } from 'site-modules/shared/constants/lease-deals/lease-deals';
import { getSortParamWithFallback } from 'site-modules/shared/utils/lease-deals/lease-deals-filter-utils';

const ALLOWED_FILTER_KEYS = Object.values(FILTER_QUERY_PARAMS);

const rangeTransformer = {
  fromUrl: value => {
    const parts = value.replace(/\*/g, '0').split('-');
    const parse = v => (Number(v) ? Number(v) : null);
    return [parse(parts[0]), parse(parts[1])];
  },
  toUrl: ([from, to]) => (!from && !to ? null : `${from || '*'}-${to || '*'}`),
};

const CUSTOM_TRANSFORMERS = {
  [FILTER_QUERY_PARAMS.MONTHLY_PAYMENT]: rangeTransformer,
  [FILTER_QUERY_PARAMS.DUE_AT_SIGNING]: rangeTransformer,
  [FILTER_QUERY_PARAMS.PAGE_NUM]: {
    fromUrl: value => [!Number(value) ? 1 : Number(value)],
    toUrl: values => (values[0] === 1 ? null : values[0]),
  },
  [FILTER_QUERY_PARAMS.DEALS]: {
    fromUrl: value => value && [value.toLowerCase()],
    toUrl: values => (values[0] === DEALS_FILTER_VALUES.ALL ? null : values[0]),
  },
  [FILTER_QUERY_PARAMS.SORT_BY]: {
    toUrl: ([sortBy]) => {
      const defaultSort = getSortParamWithFallback(null);
      return sortBy === defaultSort ? null : sortBy;
    },
  },
  default: {
    fromUrl: value => value && decodeURIComponent(value).replace(/\+/g, ' ').split(',').filter(Boolean),
    toUrl: values => values.join(','),
  },
};

/**
 * Parses a URL query object into a structured `selectedFilters` object.
 * * It only includes keys present in `ALLOWED_FILTER_KEYS` and transforms
 * comma-separated string values into arrays of "nice names".
 *
 * @param {Record<string, string | undefined>} query - A URL query object (e.g., from `req.query` or URLSearchParams).
 * @returns {Record<string, string[]>|{}} An object representing the selected filters,
 * where keys are filter names and values are arrays of strings.
 * @example
 * // returns { bodyType: ['SUV', 'Sedan'] } (if 'zip' is not an allowed key)
 * parseFiltersFromUrl({ bodyType: 'SUV,Sedan', zip: '12345' });
 */
export function parseFiltersFromUrl(query) {
  if (!query) return {};
  return ALLOWED_FILTER_KEYS.reduce((selectedFilters, filterName) => {
    const stringFilterValues = query[filterName];
    if (!stringFilterValues) return selectedFilters;
    const transform = CUSTOM_TRANSFORMERS[filterName]?.fromUrl || CUSTOM_TRANSFORMERS.default.fromUrl;

    return { ...selectedFilters, [filterName]: transform(stringFilterValues) };
  }, {});
}

/**
 * Defines custom merge strategies for specific filter keys when merging
 * routeFilters with query string params. The default strategy (overwrite)
 * is used for any key not listed here.
 *
 * Each entry is a function `(paramValue, queryValue) => string`.
 */
const ROUTE_FILTER_MERGE_STRATEGIES = {
  [FILTER_QUERY_PARAMS.BODY_TYPE]: (paramValue, queryValue) => `${paramValue},${queryValue}`,
  [FILTER_QUERY_PARAMS.ENGINE_TYPE]: (paramValue, queryValue) => `${paramValue},${queryValue}`,
  [FILTER_QUERY_PARAMS.MAKE]: (paramValue, queryValue) => `${paramValue},${queryValue}`,
};

/**
 * Serializes a `selectedFilters` object into a URL query parameter string.
 *
 * It only includes keys present in `ALLOWED_FILTER_KEYS` that have
 * non-empty array values. Values already encoded in `routeFilters` (e.g.,
 * bodyType, engineType, or make from the URL path) are excluded so the query
 * string only contains interaction filters.
 *
 * @param {Record<string, string[] | number[] | undefined>} selectedFilters - A `selectedFilters` object,
 * where keys are filter names and values are arrays of selected string values.
 * @param {Record<string, string>} [routeFilters] - URL route filters (e.g., routeFilters); values already
 *   present here will not be duplicated as query params.
 * @returns {string} A URL-encoded query string (e.g., 'bodyType=SUV%2CSedan').
 * @example
 * // returns 'bodyType=SUV%2CSedan' (if 'zip' is not an allowed key)
 * createQueryParamsFromFilters({ bodyType: ['SUV', 'Sedan'], zip: ['12345'] });
 * @example
 * // returns 'bodyType=Sedan' because 'SUV' is already present in routeFilters
 * createQueryParamsFromFilters({ bodyType: ['SUV', 'Sedan'] }, { bodyType: 'SUV' });
 */
export function createQueryParamsFromFilters(selectedFilters, routeFilters) {
  const params = new URLSearchParams();
  const paramFilters = parseFiltersFromUrl(routeFilters ?? {});

  Object.entries(selectedFilters).forEach(([filterName, values]) => {
    const isAllowedKey = ALLOWED_FILTER_KEYS.includes(filterName);
    const isValidArray = Array.isArray(values) && !!values.length;

    if (!isAllowedKey || !isValidArray) return;

    const paramValues = ROUTE_FILTER_MERGE_STRATEGIES[filterName] ? paramFilters[filterName] || [] : [];
    const filteredValues = values.filter(v => !paramValues.includes(v));

    if (!filteredValues.length) return;

    const transform = CUSTOM_TRANSFORMERS[filterName]?.toUrl || CUSTOM_TRANSFORMERS.default.toUrl;
    const paramValue = transform(filteredValues, selectedFilters);

    if (paramValue) {
      params.append(filterName, paramValue);
    }
  });

  return params.toString();
}

/**
 * Maps param values, replacing slug strings with
 * their display values. For comma-separated values, each part is mapped
 * individually; unknown slugs are left as-is.
 *
 * @param {Record<string, string> | null | undefined} params - Params object (e.g., from lease deals routes params)
 * @param {Record<string, Record<string, string>>} [map={}] - Optional map to use for slug replacement
 * @returns {Record<string, string>} Params with slug values replaced by their mapped equivalents
 * @example
 * // returns { bodyType: 'SUV,Sedan', make: 'honda' }
 * mapParamSlugsToValues({ bodyType: 'suv,sedan', make: 'honda' });
 */
export function mapParamSlugsToValues(params, map = {}) {
  if (!params) return {};
  return Object.fromEntries(
    Object.entries(params).map(([key, value]) => {
      const slugMap = map[key];
      return [key, slugMap ? (slugMap[value] ?? value) : value];
    })
  );
}

/**
 * Updates only the specified filter params in the query, preserving all other params.
 * - If filter value is non-empty array → set/replace the param
 * - If filter value is empty/null/undefined → remove the param
 * - If clearAll is true → remove all filters from the query
 * - All other params (including other filters) remain unchanged
 * - Values already encoded in route filters are excluded from the query string
 *
 * @param {object} options
 * @param {Record<string, string>} options.query - Current URL query object
 * @param {Record<string, string[] | number[] | null | undefined>} options.filters - Only the filters to update
 * @param {boolean} [options.clearAll] - When true, removes all filter params from the query
 * @param {Record<string, string>} [options.routeFilters] - URL route filters (e.g., routeFilters); values already
 *   present here will not be duplicated as query params
 * @returns {string} Updated query string
 */
export function mergeFiltersIntoQuery({ query, filters, clearAll, routeFilters }) {
  const queryObject = Object.fromEntries(new URLSearchParams(query));
  const selectedFilters = !clearAll
    ? assign(parseFiltersFromUrl(query), filters)
    : ALLOWED_FILTER_KEYS.reduce((acc, filterKey) => ({ ...acc, [filterKey]: [] }), {});

  const paramFilters = parseFiltersFromUrl(routeFilters ?? {});

  const filtersObject = Object.entries(selectedFilters).reduce((acc, [filterKey, values]) => {
    const paramValues = ROUTE_FILTER_MERGE_STRATEGIES[filterKey] ? paramFilters[filterKey] || [] : [];
    const filteredValues = Array.isArray(values) ? values.filter(v => !paramValues.includes(v)) : values;

    const transform = CUSTOM_TRANSFORMERS[filterKey]?.toUrl || CUSTOM_TRANSFORMERS.default.toUrl;
    const paramValue =
      Array.isArray(filteredValues) && filteredValues.length ? transform(filteredValues, selectedFilters) : null;

    return { ...acc, [filterKey]: paramValue };
  }, {});

  const urlParams = new URLSearchParams(omitBy(assign(queryObject, filtersObject), isNil));

  return urlParams.toString();
}

/**
 * Merges routeFilters and query string params, applying per-key merge
 * strategies defined in `ROUTE_FILTER_MERGE_STRATEGIES` when both objects contain
 * the same key. For all other keys, `query` values overwrite `params` values.
 *
 * @param {Record<string, string>} routeFilters - Location route filters (e.g., /lease-deals/ model 'routeFilters')
 * @param {Record<string, string>} query - Query string params (e.g., from getQuery)
 * @returns {Record<string, string>} Merged params object ready to pass to parseFiltersFromUrl
 * @example
 * // returns { bodyType: 'suv,pickup', make: 'honda' }
 * mergeParamsWithQuery({ bodyType: 'suv', make: 'toyota' }, { bodyType: 'pickup', make: 'honda' });
 */
export function mergeParamsWithQuery(routeFilters, query) {
  const merged = { ...routeFilters, ...query };

  Object.keys(ROUTE_FILTER_MERGE_STRATEGIES).forEach(key => {
    if (routeFilters?.[key] && query?.[key]) {
      merged[key] = ROUTE_FILTER_MERGE_STRATEGIES[key](routeFilters[key], query[key]);
    }
  });

  return merged;
}
